import type { QueryClient } from '@tanstack/react-query';
import { parseChannel } from '@xdocs/schemas';
import type { WsEnvelope } from '@xdocs/schemas';

/**
 * TanStack Query invalidation SKELETON (task spec: "WS client instance
 * wired to TanStack Query invalidation skeleton"). Each feature workstream
 * owns its own query keys; this maps the generic `push` channel → the
 * matching query-key PREFIX so `invalidateQueries` catches every query a
 * feature registers under it, without this file knowing per-op payload
 * shapes. `document:`/`job:`/`share:`/`search:` are demonstrative — no
 * feature is live yet at M0, so nothing currently listens on those keys;
 * they document the wiring contract for WS-GEN/WS-MEDIA/WS-SHARE/WS-SEARCH.
 */
export function invalidateForPush(queryClient: QueryClient, envelope: WsEnvelope): void {
  if (envelope.type !== 'push' || !envelope.channel) return;
  const parsed = parseChannel(envelope.channel);
  if (!parsed) return;

  switch (parsed.kind) {
    case 'user':
      // credit balance / notifications / session-revoked (WS-BILL/WS-NOTIF).
      void queryClient.invalidateQueries({ queryKey: ['user', parsed.id] });
      break;
    case 'document':
      void queryClient.invalidateQueries({ queryKey: ['document', parsed.id] });
      void queryClient.invalidateQueries({ queryKey: ['documents', 'list'] });
      break;
    case 'job':
      void queryClient.invalidateQueries({ queryKey: ['job', parsed.id] });
      break;
    case 'share':
      void queryClient.invalidateQueries({ queryKey: ['share', parsed.id] });
      break;
    case 'search':
      void queryClient.invalidateQueries({ queryKey: ['search', parsed.id] });
      break;
    case 'outline-confirm':
    case 'presence':
    case 'deploy-broadcast':
    case 'admin-contact-inbox':
    case 'server-shutdown':
      // No query-backed cache to invalidate — consumed directly by feature
      // hooks (deploy toast, presence indicator, outline-confirm modal,
      // graceful-shutdown reconnect hint).
      break;
  }
}
